import {
    PageType,
    Device,
    L1Category,
    L2Category,
    L3Category,
} from '@gumtree/shared/src/types/client-data';
import { DfpServerConfig, GoogleTag } from '@gumtree/shared/src/types/advertising/dfp';
import { runOnBreakpointChange, ScreenSize } from '@gumtree/shared/src/util/breakpoints-service';
import type { Pbjs } from '@gumtree/shared/src/types/advertising/prebid';
import { getGrowthbookClientSide } from '@gumtree/shell/src/growthbook/init-growthbook';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';

import { CommandFactoryParams } from './types';
import preDfpRequestActions from './common/pre-dfp-request-actions';
import { getWhitelistedSlotIds } from '../dfp/refresh-whitelist';
import { doesPrebidExistOnPage } from './util';
import { adPerformanceTracker } from '../tracking/ad-performance-tracker';

export interface WindowWithPbjs extends Window {
    pbjs?: { que?: { push: object }; bidderSettings?: object };
    googletag?: { cmd?: { push: object } };
}

const refreshAds = (commandFactoryParams: CommandFactoryParams) => {
    const adsReloadDelay = 20;
    const maxRefreshes = 3;
    let refreshCount = 0;

    const { deviceType, categoryL1, pageType, screenSize } = commandFactoryParams;

    const whiteListedSlotIds = getWhitelistedSlotIds(deviceType, categoryL1, pageType, screenSize);

    if (whiteListedSlotIds && whiteListedSlotIds.length > 0) {
        const refreshId = setInterval(() => {
            preDfpRequestActions(commandFactoryParams, true).then(() => {
                window.document.dispatchEvent(new Event('GUM.ADS.DISPLAY.REFRESH'));
            });

            if (++refreshCount >= maxRefreshes) {
                clearInterval(refreshId);
            }
        }, adsReloadDelay * 1000);
    }
};

const initPrebid = (window: WindowWithPbjs) => {
    window.pbjs = window.pbjs || {};
    window.pbjs.que = window.pbjs.que || [];

    window.pbjs.bidderSettings = {
        appnexus: {
            adserverTargeting: [
                {
                    key: 'hb_deal_appnexus',
                    val: (bidResponse) =>
                        bidResponse.appnexus ? bidResponse.appnexus.dealCode : undefined,
                },
            ],
        },
    };
};

const startPrebid = async (
    googletag: GoogleTag,
    pageType: PageType,
    deviceType: Device,
    screenSize: ScreenSize | undefined,
    categoryL1: L1Category | undefined,
    categoryL2: L2Category | undefined,
    categoryL3: L3Category | undefined,
    keywords: string,
    serverConfig: DfpServerConfig | undefined,
    window: WindowWithPbjs
) => {
    const isDisplayValid =
        typeof serverConfig === 'object' &&
        typeof serverConfig.config === 'object' &&
        typeof window.googletag === 'object' &&
        typeof window.googletag.cmd === 'object' &&
        typeof window.googletag.cmd.push === 'function';

    if (!doesPrebidExistOnPage(pageType) && isDisplayValid) {
        window.document.dispatchEvent(new Event('GUM.ADS.DISPLAY.LOAD'));
        return;
    }

    initPrebid(window);

    const isPrebidValid =
        typeof serverConfig?.config?.prebid === 'object' &&
        typeof window.pbjs === 'object' &&
        typeof window.pbjs.que === 'object' &&
        typeof window.pbjs.que.push === 'function';

    if (!isPrebidValid && !isDisplayValid) {
        return;
    }

    if (!isPrebidValid && isDisplayValid) {
        // ensure event listeners are defined previously
        window.document.dispatchEvent(new Event('GUM.ADS.DISPLAY.LOAD'));
        return;
    }
    const prebidTimeoutConfs = {
        'A': 600,
        'B': 800,
        'C': 1000,
        'D': 1200,
        'Default': 1000,
    }
    const growthbookClient = getGrowthbookClientSide();
    const prebidTimeoutKey = growthbookClient.getFeatureValue(GrowthBookFeature.PREBID_TIMEOUT, 'out');
    const prebidTimeout = prebidTimeoutConfs[prebidTimeoutKey] || prebidTimeoutConfs['Default'];

    const commandFactoryParams: CommandFactoryParams = {
        pbjs: window.pbjs as Pbjs,
        timeout: prebidTimeout,
        googletag,
        pageType,
        deviceType,
        screenSize: screenSize as ScreenSize,
        categoryL1: categoryL1 as L1Category,
        categoryL2,
        categoryL3,
        keywords,
    };

    runOnBreakpointChange(() => {
        adPerformanceTracker.markPrebidStart();
        preDfpRequestActions(commandFactoryParams).then(() => {
            adPerformanceTracker.markPrebidComplete();
            window.document.dispatchEvent(new Event('GUM.ADS.DISPLAY.LOAD'));
        });
    });

    adPerformanceTracker.markPrebidStart();
    await preDfpRequestActions(commandFactoryParams);
    adPerformanceTracker.markPrebidComplete();
    window.document.dispatchEvent(new Event('GUM.ADS.DISPLAY.LOAD'));

    refreshAds(commandFactoryParams);
};

export default startPrebid;
